import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Pagination } from "@mui/material";
import { menuInfo } from "@shared/type/CommonType";
import { getCodeData, getInitFormData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import SharedForm from "@widgets/SharedForm";
import { SnResizable } from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import SN_SYS_BAS_REP_001_POP_001 from "./SN_SYS_BAS_REP_001_POP_001";
import { getAttachFileBlob } from "./SN_SYS_BAS_REP_001_api";
import SN_SYS_BAS_REP_001_POP_002 from "./SN_SYS_BAS_REP_001_POP_002";
import SN_SYS_BAS_REP_001_PREVIEW from "./SN_SYS_BAS_REP_001_PREVIEW";

interface Props {
  Menuprops: menuInfo;
}

// zip 안에 같은 이름이 있으면 "파일명 (1).확장자" 형태로 붙여 덮어쓰기를 막는다.
const getUniqueFileName = (fileName: string, usedNames: Set<string>) => {
  if (!usedNames.has(fileName)) {
    usedNames.add(fileName);
    return fileName;
  }

  const idx = fileName.lastIndexOf(".");
  const name = idx < 0 ? fileName : fileName.slice(0, idx);
  const ext = idx < 0 ? "" : fileName.slice(idx);

  let seq = 1;
  let unique = `${name} (${seq})${ext}`;
  while (usedNames.has(unique)) {
    seq++;
    unique = `${name} (${seq})${ext}`;
  }

  usedNames.add(unique);
  return unique;
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

// 전표일자(dt_docu)를 그리드에 넣기 전에 정리한다.
// SnDateBox 컬럼 포맷터는 값이 truthy 이기만 하면 dayjs 로 넘기고, 파싱에 실패하면
// "Invalid Date" 를 그대로 셀에 찍는다. 공백만 든 문자열("   ")이 대표적인 경우다.
// 그래서 yyyyMMdd 8자리가 아니면 빈 값으로 떨궈 빈칸으로 보이게 한다.
// (숫자로 내려오는 경우도 문자열로 맞춰 준다. dayjs 가 숫자를 epoch ms 로 읽어 1970년이 되는 걸 막는다)
const normalizeDtDocu = (value: any) => {
  const text = String(value ?? "").trim();
  return /^\d{8}$/.test(text) ? text : "";
};

// 파일유형 공통코드 (mas_codel.cd_clas) — 코드 EXCEL/WORD/… 를 명칭으로 바꿀 때 쓴다.
const FILE_TYPE_CLAS = "SYS054";

// 바이트 값을 KB 로 올림해 표시한다. 1KB 미만 파일도 0KB 대신 1KB 로 보여 준다.
const toKiloBytes = (value: any) => {
  if (value === null || value === undefined || value === "") return "";
  const bytes = Number(value);
  if (Number.isNaN(bytes)) return String(value);
  if (bytes <= 0) return "0 KB";
  return `${Math.max(1, Math.ceil(bytes / 1024)).toLocaleString("ko-KR")} KB`;
};

// 문서검색
const SN_SYS_BAS_REP_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });
  const sharedFormRef = useRef<any>(null);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  // 파일유형(tp_file) 코드 → 명칭. 그리드는 코드(EXCEL)를 내려받으므로 표시할 때 바꿔준다.
  // 공통코드 SYS054(mas_codel: cd_flag/nm_flag)를 그대로 쓴다.
  const [fileTypeNames, setFileTypeNames] = useState<Record<string, string>>(
    {}
  );

  // 우측 상세 프리폼. 항목 구성은 sys_formctrl(cd_form='freeform1')에서 내려온다.
  // 그리드에서 포커스된 행의 상세를 계속 따라 보여준다.
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>(null);

  const cd_menu = Menuprops?.cd_menu || "SN_SYS_BAS_REP_001";
  // 스플릿 대상은 그리드(panel1)뿐이다. 문서정보는 남는 폭을 채운다.
  const defaultSize = { panel1width: "70%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);

  // 서버 페이징 상태. page는 1부터 시작한다.
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
        });
        setSharedForm(rtn.form?.freeform1 || []);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    let alive = true;
    const loadFileTypes = async () => {
      try {
        const rows: any = await getCodeData(FILE_TYPE_CLAS, "");
        if (!alive || !Array.isArray(rows)) return;
        setFileTypeNames(
          Object.fromEntries(
            rows
              .filter((r: any) => r?.code)
              .map((r: any) => [String(r.code), String(r.name ?? "")])
          )
        );
      } catch {
        /* 코드조회 실패하면 코드 원문이 그대로 보인다 */
      }
    };
    loadFileTypes();
    return () => {
      alive = false;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  // 서버 페이징. 조회 SQL 에 page_size/offset 을 넘기고,
  // 총 건수는 COUNT(*) OVER() 결과(qt_total)를 각 행에서 받아 페이저를 그린다.
  const fetchPage = async (nextPage: number, nextPageSize: number) => {
    try {
      const params = {
        id: "sn_sys_bas_rep_001_01",
        params: {
          ...searchRef.current.getFormdata(),
          page_size: nextPageSize,
          offset: (nextPage - 1) * nextPageSize,
        },
      };

      gridRefs.current["grid1"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      const rows: any[] = (data[0] || []).map((row: any) => ({
        ...row,
        dt_docu: normalizeDtDocu(row?.dt_docu),
      }));

      setDataSources({ grid1: rows });
      // 새로 조회하면 이전 행의 상세는 지운다. (행 포커스가 잡히면 다시 채워진다)
      setFocusedRowData(null);
      // 조회 결과가 없으면 총 건수도 0. (qt_total 은 bigint 라 문자열로 올 수 있어 Number 로 맞춘다)
      setTotalCount(Number(rows[0]?.qt_total ?? 0));
      setPage(nextPage);
      setPageSize(nextPageSize);
    } catch (error: any) {}
  };

  // 조회 버튼은 조건이 바뀐 것으로 보고 항상 1페이지부터 다시 읽는다.
  const onSearch = async () => {
    await fetchPage(1, pageSize);
  };

  // 단건은 원본 파일 그대로, 복수 건은 zip으로 묶어 내려받는다.
  // (파일마다 저장 창이 뜨면 브라우저가 다중 다운로드를 차단할 수 있어 zip으로 만든다)
  const onDownload = async (rows: any[]) => {
    try {
      if (rows.length === 1) {
        const blob = await getAttachFileBlob(rows[0]);
        saveAs(blob, String(rows[0]?.nm_file || "download"));
        return;
      }

      const zip = new JSZip();
      const usedNames = new Set<string>();
      const failedNames: string[] = [];
      let successCount = 0;

      for (const row of rows) {
        const nmFile = String(row?.nm_file || "");
        try {
          const blob = await getAttachFileBlob(row);
          zip.file(
            getUniqueFileName(nmFile || `file_${row?.id}`, usedNames),
            blob
          );
          successCount++;
        } catch (error) {
          console.error("파일 다운로드를 실패했습니다.", error);
          failedNames.push(nmFile);
        }
      }

      if (successCount < 1) {
        await showDialog("error", "파일 다운로드를 실패했습니다.");
        return;
      }

      const now = new Date();
      const dt = now.toISOString().slice(0, 10).replace(/-/g, "");
      const tm = now.toTimeString().slice(0, 8).replace(/:/g, "");
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `문서검색[${dt}${tm}].zip`);

      if (failedNames.length > 0) {
        await showDialog(
          "alter",
          `다음 파일을 받지 못했습니다.\n${failedNames.join(", ")}`
        );
      }
    } catch (error) {
      console.error("파일 다운로드를 실패했습니다.", error);
      await showDialog("error", "파일 다운로드를 실패했습니다.");
    }
  };

  // 체크한 파일들을 팝업으로 띄우고, 팝업 안에서 파일을 골라 내용을 본다.
  const onPreview = async (rows: any[]) => {
    await showPopupModal({
      title: "미리보기",
      children: <SN_SYS_BAS_REP_001_POP_002 rows={rows} />,
      width: "70%",
      height: "90%",
      okayBtn: false,
    });
  };

  const onGrid1BtnsClick = async (id: string) => {
    const checkedRows: any[] =
      gridRefs.current["grid1"].current?.getCheckedRows() || [];

    if (id === "btnDownload") {
      // 다운로드 대상은 체크박스로 체크한 행이다.
      if (checkedRows.length < 1) {
        await showDialog("alter", "체크된 문서가 없습니다.");
        return;
      }
      await onDownload(checkedRows);
    }

    if (id === "btnPreview") {
      // 미리보기는 체크한 행이 있으면 그 행들을, 없으면 클릭(포커스)한 행을 본다.
      const targetRows =
        checkedRows.length > 0
          ? checkedRows
          : focusedRowData
          ? [focusedRowData]
          : [];
      if (targetRows.length < 1) {
        await showDialog("alter", "미리볼 문서를 선택해 주세요.");
        return;
      }
      await onPreview(targetRows);
    }
  };

  // 포커스된 행이 바뀔 때마다 우측 프리폼을 그 행 기준으로 갱신한다.
  const onFocusedRowChanged = useCallback((rowData: any | null) => {
    setFocusedRowData(rowData);
  }, []);

  // 프리폼에 넘길 값. 크기는 SnNumberBox 가 우측정렬 고정이라 SnTextBox 로 두고,
  // 값만 그리드와 같은 KB 표기로 만들어 넘긴다.
  // (원본 qt_size 가 필요한 곳이 있으므로 focusedRowData 자체는 가공하지 않는다)
  const sharedFormData = useMemo(() => {
    if (!focusedRowData) return null;
    return {
      ...focusedRowData,
      qt_size: toKiloBytes(focusedRowData.qt_size),
    };
  }, [focusedRowData]);

  const grids = useMemo(
    () => ({
      grid1: {
        // 크기는 바이트로 내려오므로 표시할 때만 KB 로 바꾼다.
        // 값 자체는 그대로 둬야 정렬이 숫자 기준으로 동작한다.
        //
        // tp_column 을 SnTextBox 로 바꿔서 넘기는 이유:
        // SnColumnsCreate 는 유형별 기본 설정을 사용자 valueFormatter 뒤에 얹으므로
        // SnNumberBox 로 두면 유형 기본 포맷터(천단위 콤마)가 KB 포맷터를 덮어쓴다.
        // 조회 전용 컬럼이라 편집기/필터 유형이 바뀌어도 화면 동작에는 영향이 없다.
        // 다만 SnTextBox 는 정렬 지정이 없어 왼쪽으로 붙으므로 cellStyle 로 직접 돌려준다.
        //
        // 파일유형은 코드(EXCEL)로 내려오므로 명칭(Excel)으로 바꿔 보여 준다.
        // 코드를 못 찾으면 원문을 그대로 둔다.
        columns: (columns.grid1 || []).map((col: any) => {
          if (col.cd_column === "qt_size") {
            return {
              ...col,
              nm_column: "크기(KB)",
              tp_column: "SnTextBox",
              cellStyle: { textAlign: "right" },
              valueFormatter: (params: any) => toKiloBytes(params.value),
            };
          }
          if (col.cd_column === "tp_file") {
            return {
              ...col,
              // SnComboBox 로 두면 유형 기본 cellRenderer 가 js_combo 만 보고 명칭을 찾다가
              // 못 찾으면 코드를 그대로 찍는다. cellRenderer 는 valueFormatter 를 이기므로
              // 아래 포맷터가 먹도록 크기 컬럼과 같은 방식으로 SnTextBox 로 넘긴다.
              tp_column: "SnTextBox",
              valueFormatter: (params: any) => {
                const code = String(params.value ?? "");
                return fileTypeNames[code] || code;
              },
            };
          }
          return col;
        }),
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
        },
      },
    }),
    [columns, dataSources, fileTypeNames, onFocusedRowChanged]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 flex flex-col gap-1.5 min-h-0">
        <div className="flex-1 min-h-0 flex flex-row space-x-1.5">
          <SnResizable
            direction={"width"}
            seq={1}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel1width}
            onResizeStop={onResizeStop}
            enable={{ right: true }}
            minWidth={300}
          >
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{
                rowSelection: "multiple",
                ischeckboxSelection: true,
                title: "검색결과",
              }}
              events={grids.grid1.events}
              header={
                <div>
                  <SnButton
                    text={"다운로드"}
                    onClick={() => {
                      onGrid1BtnsClick("btnDownload");
                    }}
                  />
                  <SnButton
                    text={"미리보기"}
                    onClick={() => {
                      onGrid1BtnsClick("btnPreview");
                    }}
                  />
                </div>
              }
            />
          </SnResizable>

          {/* 문서정보는 폭을 따로 갖지 않고 그리드가 내준 만큼 채운다.
              스플릿바(그리드 오른쪽 핸들)를 왼쪽으로 끌면 그리드가 줄고
              그만큼 문서정보가 그리드 쪽으로 넓어진다. */}
          <div
            className="h-full flex flex-col"
            style={{ flex: "1 1 0%", minWidth: 240 }}
          >
            <SharedForm
              ref={sharedFormRef}
              data={sharedFormData}
              controls={sharedForm}
              gridref={gridRefs.current["grid1"]}
              title="문서정보"
              autoFocus={false}
              // 항목들 아래에 포커스된 행의 파일 내용을 바로 보여준다.
              // (미리보기 버튼 팝업과 같은 컴포넌트를 프리폼 그리드 흐름에 맞춰 쓴다)
              footer={
                <SN_SYS_BAS_REP_001_PREVIEW
                  row={focusedRowData}
                  variant="form"
                />
              }
            />
          </div>
        </div>

        {totalCount > 0 && (
          <div className="flex items-center justify-center gap-2 py-1">
            <span className="text-xs text-gray-600">
              총 {totalCount.toLocaleString()}건
            </span>
            <Pagination
              size="small"
              shape="rounded"
              showFirstButton
              showLastButton
              count={Math.max(1, Math.ceil(totalCount / pageSize))}
              page={page}
              onChange={(_, value) => fetchPage(value, pageSize)}
            />
            <select
              className="text-xs border border-gray-300 rounded px-1 py-0.5"
              value={pageSize}
              onChange={(e) => fetchPage(1, Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}건씩
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
});

export default SN_SYS_BAS_REP_001;
